const MANUAL_ORDERED_LIST_ITEM = /^([\s\u200B]+)(\d+)[.)]\s+/;

const removeLeadingText = (element: Element, characterCount: number) => {
  const walker = element.ownerDocument.createTreeWalker(
    element,
    NodeFilter.SHOW_TEXT
  );
  let remaining = characterCount;
  let currentNode: Node | null = null;

  while (remaining > 0 && (currentNode = walker.nextNode())) {
    const text = currentNode.textContent ?? "";

    if (text.length <= remaining) {
      remaining -= text.length;
      currentNode.textContent = "";
      continue;
    }

    currentNode.textContent = text.slice(remaining);
    remaining = 0;
  }
};

/**
 * Word documents sometimes contain visually indented, manually typed numbered
 * lists instead of real numbering definitions. Browsers collapse the leading
 * spaces in Mammoth's HTML, so preserve those runs as plain paragraphs while
 * replacing their leading spaces with a tab that survives Quill conversion.
 */
export const useNormalizeWordDocumentLists = (html: string): string => {
  const parser = new DOMParser();
  const document = parser.parseFromString(`<div>${html}</div>`, "text/html");
  const container = document.querySelector("div");

  if (!container) return html;

  const children = Array.from(container.children);

  for (let index = 0; index < children.length; index++) {
    const firstParagraph = children[index];
    if (firstParagraph.tagName !== "P") continue;

    const firstMatch = (firstParagraph.textContent ?? "").match(
      MANUAL_ORDERED_LIST_ITEM
    );
    if (!firstMatch || Number(firstMatch[2]) !== 1) continue;

    const paragraphs: Element[] = [firstParagraph];
    let expectedNumber = 2;

    for (let nextIndex = index + 1; nextIndex < children.length; nextIndex++) {
      const nextParagraph = children[nextIndex];
      if (nextParagraph.tagName !== "P") break;

      const match = (nextParagraph.textContent ?? "").match(
        MANUAL_ORDERED_LIST_ITEM
      );
      if (!match || Number(match[2]) !== expectedNumber) break;

      paragraphs.push(nextParagraph);
      expectedNumber++;
    }

    if (paragraphs.length < 2) continue;

    for (const paragraph of paragraphs) {
      const leadingWhitespace = (paragraph.textContent ?? "").match(
        MANUAL_ORDERED_LIST_ITEM
      )?.[1];
      if (!leadingWhitespace) continue;

      removeLeadingText(paragraph, leadingWhitespace.length);
      paragraph.insertBefore(
        document.createTextNode("\u200B\t"),
        paragraph.firstChild
      );
    }

    index += paragraphs.length - 1;
  }

  return container.innerHTML;
};
